import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ItemFinancials, SetItemFinancialsInput } from '@/common/api/types/itemFinancials';
import { financeQueryKeys } from '@/common/queryKeys/finance';
import { ItemFinancialsApi } from '@/features/financials/api/item-financials';

const [ITEM_FINANCIALS_QUERY_KEY] = financeQueryKeys.itemFinancials.all();

export const useSetItemFinancialsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: number; input: SetItemFinancialsInput }) =>
      ItemFinancialsApi.setItemFinancials(itemId, input),
    onSuccess: (updated, { input }) => {
      queryClient.setQueriesData<ItemFinancials[]>(
        {
          predicate: ({ queryKey }) => {
            const requestedIds = queryKey[2];
            return (
              queryKey[0] === ITEM_FINANCIALS_QUERY_KEY &&
              Array.isArray(requestedIds) &&
              requestedIds.includes(updated.itemId)
            );
          },
        },
        (old) =>
          old
            ? [...old.filter((financials) => financials.itemId !== updated.itemId), updated]
            : old,
      );
      // Lets VisibleItemFinancialsContext's paged cache pick up this write via
      // useCrossCachePagesSync, without waiting for its page query to refetch.
      queryClient.setQueryData(financeQueryKeys.itemFinancials.byItemId(updated.itemId), updated);
      // CapEx % and forecast revenue are both resolved server-side in reporting-store (root vs.
      // inherited), so a write to either can change the effective value for this item's whole
      // hierarchy, not just itself — refetch rather than trying to patch every affected
      // descendant's cached aggregate locally.
      if (input.capexPercentage !== undefined || input.forecastRevenueAmount !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: financeQueryKeys.itemFinancialsAggregate.all(),
        });
      }
    },
  });
};
